import { useCallback, useState } from 'react';

import { useDebounceCallback } from '@xemplo/hooks';

/**
 * When implementing the tag field element, you will likely need some
 * server-side logic to handle tag creation and searching. This hook
 * provides the things that would be hard to figure it out you actually
 * need them. The wrappers for tag creation and open state are included
 * to ensure a smooth UX when dealing with server-side data.
 */
export function useServerSideHelper(debounceTime = 300) {
  // Manage search state for server-side tag fetching
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounceCallback(setSearch, debounceTime);

  // Reset search when a new tag is created
  // This ensures the query is called with empty search
  // so when the logic to select the newly created tag runs
  // within the tag component itself, it has the full list available.
  const beforeTagCreate = useCallback(() => setSearch(''), []);

  // We need to clear the search so every time you open the tag field,
  // it shows the full list of tags, otherwise a cached list would be shown.
  const onOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSearch('');
    }
  }, []);

  return {
    search,
    debouncedSearch,
    beforeTagCreate,
    onOpenChange,
  };
}
